
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import { StatusCode } from "api/protocols";
import { useTranslation } from "react-i18next";
import { GetDepartments, PostDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";

type DepartmentType = "department" | "category" | "subcategory";

export default function NewDepartment() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { type } = useParams<{ type: string }>();

  const departmentType: DepartmentType =
    type === "department" || type === "category" || type === "subcategory" ? type : "department";

  const showParentSelect = departmentType !== "department";

  const [title, setTitle] = useState("");
  const [parentId, setParentId] = useState<number | undefined>(undefined);
  const [allDepartments, setAllDepartments] = useState<any[]>([]);

  useEffect(() => {
    setParentId(undefined);
    if (showParentSelect) {
      void loadDepartments();
    }
  }, [departmentType]);

  async function loadDepartments() {
    const res = await GetDepartments({ extended: true }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }
    setAllDepartments(res.data ?? []);
  }

  const departments = allDepartments.filter((dept: any) => dept.type === "department");

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setTitle(event.target.value);
  }

  function handleParentChange(value: number) {
    setParentId(value);
  }

  const isSaveDisabled =
    departmentType === "department" ? !title : !title || parentId === undefined || parentId === null;

  async function saveNew() {
    if (isSaveDisabled) {
      return;
    }

    const parent_id = showParentSelect ? parentId : undefined;
    const res = await PostDepartments({ title, type: departmentType, parent_id }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/departments");
  }

  function handleCancel() {
    history.push("/admin/settings/departments");
  }

  function renderParentOptions() {
    if (departmentType === "category") {
      return departments.map((department: any, index: number) => (
        <Option key={index} value={department.id} name={department.title}>
          {department.title}
        </Option>
      ));
    }

    if (departmentType === "subcategory") {
      return departments.map((department: any, index: number) => (
        <React.Fragment key={index}>
          <p style={{ padding: "8px 16px" }} className="text-md text-semibold">
            {department.title}
          </p>
          {department.cateogries?.map((category: any, catIndex: number) => (
            <Option key={`${index}-${catIndex}`} value={category.id} name={category.title}>
              {category.title}
            </Option>
          ))}
        </React.Fragment>
      ));
    }

    return null;
  }

  const basePath = "/admin/settings/departments";

  return (
    <Page
      title={t(
        departmentType === "department"
          ? "Create new department"
          : departmentType === "category"
          ? "Create new category"
          : "Create new subcategory",
      )}
      subtitle={t(
        departmentType === "department"
          ? "Manage which input fields will appear when staff create a new product in this department."
          : departmentType === "category"
          ? "Manage which input fields will appear when staff create a new product in this cateogry."
          : "Segment specific product variations.",
      )}
      primaryAction={{
        content: t(
          departmentType === "department"
            ? "Create department"
            : departmentType === "category"
            ? "Create category"
            : "Create subcategory",
        ),
        action: saveNew,
        disabled: isSaveDisabled,
      }}
      secondaryActions={[
        {
          content: t("Cancel"),
          action: handleCancel,
        },
      ]}
      breadcrumbs={[
        { label: t("Settings"), url: "/admin/settings" },
        { label: t("Departments & categories"), url: basePath },
        {
          label: t(
            departmentType === "department"
              ? "Create department"
              : departmentType === "category"
              ? "Add new category"
              : "Create new subcategory",
          ),
          url: `${basePath}/${departmentType}/new`,
        },
      ]}
    >
      <Card
        title={t(
          departmentType === "department"
            ? "Department details"
            : departmentType === "category"
            ? "Category details"
            : "Subcategory details",
        )}
      >
        <Card.Section>
          <Input
            value={title}
            label={t(
              departmentType === "department"
                ? "Department name"
                : departmentType === "category"
                ? "Category name"
                : "Subcategory name",
            )}
            id="title"
            onChange={handleInputChange}
            placeholder={t(
              departmentType === "department"
                ? "Enter department name"
                : departmentType === "category"
                ? "Enter category name"
                : "Enter subcategory name",
            )}
          />

          {showParentSelect && (
            <div className="mt-4">
              <Select
                label={t(departmentType === "category" ? "Select parent department" : "Choose parent category")}
                onChange={handleParentChange}
                placeholder={t(departmentType === "category" ? "Select parent department" : "Select parent category.")}
                helpText={
                  departmentType === "category"
                    ? t("Assign this category to a department (e.g. Golf, F&B).")
                    : departmentType === "subcategory"
                    ? t("Select a category that this variation belongs to.")
                    : undefined
                }
              >
                {renderParentOptions()}
              </Select>
            </div>
          )}
        </Card.Section>
      </Card>
    </Page>
  );
}
